<template>
  <div
    class="scheduled-export-setting-card"
  >
    <div class="card-header">
      <h4 class="setting-name">{{ setting.fileName }}</h4>
    </div>

    <div class="card-content">
      <div class="info-row">
        <span class="label">{{ $t('leadExport.executionSchedule') }}:</span>
        <span class="value">{{ scheduleText }}</span>
      </div>

      <div class="info-row">
        <span class="label">{{ $t('leadExport.fileType.title') }}:</span>
        <span class="value">{{ fileTypeText }}</span>
      </div>

      <div class="info-row">
        <span class="label">{{ $t('leadExport.dataFormat.title') }}:</span>
        <span class="value">{{ dataFormatText }}</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { camelCase } from 'lodash';
import { PartLeadExportSettingScheduledIntervalEnum, ScheduledLeadExportSetting } from '@/api/openapi';

type TProps = {
  setting: ScheduledLeadExportSetting;
};

const props = defineProps<TProps>();
const i18n = useI18n();

const scheduleText = computed(() => {
  const interval = i18n.t(`leadExport.interval.enum.${camelCase(props.setting.interval)}`);
  const time = props.setting.time;

  let details = '';

  if (props.setting.interval === PartLeadExportSettingScheduledIntervalEnum.Weekly) {
    if (props.setting.daysOfWeek) {
      const dayTexts = props.setting.daysOfWeek.map(day => {
        const dayValue = camelCase(day);

        if (dayValue === 'sunday') {
          return i18n.t('general.dayOfWeek.enum.sunday');
        } else if (dayValue === 'monday') {
          return i18n.t('general.dayOfWeek.enum.monday');
        } else if (dayValue === 'tuesday') {
          return i18n.t('general.dayOfWeek.enum.tuesday');
        } else if (dayValue === 'wednesday') {
          return i18n.t('general.dayOfWeek.enum.wednesday');
        } else if (dayValue === 'thursday') {
          return i18n.t('general.dayOfWeek.enum.thursday');
        } else if (dayValue === 'friday') {
          return i18n.t('general.dayOfWeek.enum.friday');
        } else if (dayValue === 'saturday') {
          return i18n.t('general.dayOfWeek.enum.saturday');
        } else {
          return '';
        }
      });
      details = ` (${dayTexts.join(', ')})`;
    }
  } else if (props.setting.interval === PartLeadExportSettingScheduledIntervalEnum.Monthly) {
    if (props.setting.daysOfMonth) {
      details = ` (${props.setting.daysOfMonth.map(day => `${day}日`).join(', ')})`;
    }
  }

  return `${interval}${details} ${time}`;
});

const fileTypeText = computed(() => {
  if (!props.setting.fileType) {
    return '';
  }

  const fileTypeValue = camelCase(props.setting.fileType);

  if (fileTypeValue === 'excel') {
    return i18n.t('leadExport.fileType.enum.excel');
  } else if (fileTypeValue === 'csv') {
    return i18n.t('leadExport.fileType.enum.csv');
  } else {
    return '';
  }
});

const dataFormatText = computed(() => {
  if (!props.setting.dataFormat) {
    return '';
  }

  const dataFormatValue = camelCase(props.setting.dataFormat);

  if (dataFormatValue === 'standard') {
    return i18n.t('leadExport.dataFormat.enum.standard');
  } else if (dataFormatValue === 'allActivity') {
    return i18n.t('leadExport.dataFormat.enum.allActivity');
  } else {
    return ''
  }
});
</script>

<style lang="scss" scoped>
.scheduled-export-setting-card {
  border: 1px solid $bdcolor-base;
  border-radius: 8px;
  padding: 16px;
  background: $bgcolor-white;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: $basecolor-primary;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;

  .setting-name {
    margin: 0;
    font-size: 16px;
    font-weight: 600;
    color: $textcolor-base;
  }
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.info-row {
  display: flex;
  gap: 8px;

  .label {
    font-size: 12px;
    color: $textcolor-light;
    min-width: 120px;
    flex-shrink: 0;
  }

  .value {
    font-size: 12px;
    color: $textcolor-base;
    flex: 1;
  }
}
</style>
